import { createSlice } from '@reduxjs/toolkit';
import { OpportunityState } from './opportunity.state';
import { GetOpportunities } from './thunks/get-opportunities.thunk';
import { GetOpportunityDocuments } from './thunks/get-opportunity-documnts.thunk';
import { MarketplaceOpportunityStatus } from './models/opportunity-status';
import { GetOpportunity } from './thunks/get-opportunity.thunk';
import { MarketplaceOpportunity } from './models/opportunity';
import { GetCurrentEventsOfAllOpportunities } from './thunks/get-current-envents-of-all-opp.thunk';
import { GetAvailableOpportunitiesForTeamMembers } from './thunks/get-availalble-opportunities-for-team-members.thunk';
import {
    ExternalPermissions,
    InternalPermissions,
    TeamMemberType,
} from '../../../containers/Admin/BuyerMarketplace/MyTeamMembers/Models/Enums';
import {
    getFilteredAndSortedOpportunities,
    updateTableData,
} from './helpers/opportunities-table-helpers';
import { ServiceFocus } from '../../service-focuses/models/service-focus';

const defaultPageNumber = 1;
const defaultTotalItems = 1;
const defaultPageSize = 5;

const initialState: OpportunityState = {
    opportunities: {
        opportunities: [],
        data: [],
        pagination: {
            page: 1,
            sizePerPage: 5,
            totalSize: 0,
        },
        sortOrder: {
            dataField: 'listingDate',
            order: 'asc',
        },
        filters: {},
    },
    availableOpportunitiesForTeamMembers: [],
    selectedOpportuity: {
        isBuyerChatsEnabled: false,
        opportunityId: 0,
        isIdNdaSigned: false,
        isDataNdaSigned: false,
        dealStage: '',
        type: '',
        practices: [],
        listingDate: new Date(),
        status: MarketplaceOpportunityStatus.NewOpportunity,
        levelOfService: 0,
        nextDeadline: new Date(),
        isTeaserPosted: false,
        isEngageAvailable: false,
        description: '',
        events: [],
        engagementData: {
            id: 0,
            documentsList: [],
            documentSectionsList: [],
            ndaTermSelectionList: [],
            loiAndDdNdaExecutionConfigList: [],
            teaserData: [],
            dataRequestProjects: [],
            isTeaserConfirmed: false,
        },
        documentTables: {},
        engagementFteDvms: 0,
        startEngagementFteDvms: 0,
        startEngagementLtmRevenue: 0,
        teamMemberType: TeamMemberType.Internal,
        internalPermissions: InternalPermissions.ReadOnly,
        externalPermissions: ExternalPermissions.Vdr,
        isPrimaryBuyer: false,
        hasActingRepresentative: false,
        ownerName: '',
    },
    currentEventsOfAllOpportunities: {
        events: [],
        pageNumber: defaultPageNumber,
        totalItems: defaultTotalItems,
        pageSize: defaultPageSize,
    },
    isLoading: false,
    status: 'succeeded',
    error: '',
};

const opportunitiesSlice = createSlice({
    name: 'opportunities',
    initialState,
    reducers: {
        resetState(state) {
            state.opportunities = initialState.opportunities;
            state.selectedOpportuity = initialState.selectedOpportuity;
            state.isLoading = initialState.isLoading;
            state.error = initialState.error;
            state.status = initialState.status;
        },
        setSelectedOpportunity(state, action) {
            const id = action.payload;
            const opportunity = state.opportunities.opportunities.find(
                (opp) => opp.opportunityId === id
            );
            if (opportunity) {
                state.selectedOpportuity = opportunity;
            }
        },
        setTableState(state, action) {
            const {
                pagination,
                sortOrder,
                filters,
                serviceFocuses = [],
            } = action.payload;

            if (pagination) {
                state.opportunities.pagination = {
                    ...state.opportunities.pagination,
                    ...pagination,
                };
            }

            if (sortOrder) {
                state.opportunities.sortOrder = sortOrder;
            }

            if (filters !== undefined) {
                state.opportunities.filters = filters;
            }

            updateTableData(state, serviceFocuses);
        },
        setPagination(state, action) {
            const { pagination, serviceFocuses = [] } = action.payload;

            state.opportunities.pagination = {
                ...state.opportunities.pagination,
                ...pagination,
            };
            updateTableData(state, serviceFocuses);
        },
        setSortOrder(state, action) {
            const { sortOrder, serviceFocuses = [] } = action.payload;

            state.opportunities.sortOrder = sortOrder;
            state.opportunities.pagination.page = 1;
            updateTableData(state, serviceFocuses);
        },
        setFilters(state, action) {
            const { filters, serviceFocuses = [] } = action.payload;

            state.opportunities.filters = filters;
            state.opportunities.pagination.page = 1;
            updateTableData(state, serviceFocuses);
        },
        navigateTableToOpportunity(state, action) {
            const { opportunityId, serviceFocuses = [] } = action.payload;

            const filteredAndSorted = getFilteredAndSortedOpportunities(
                state,
                serviceFocuses
            );

            const index = filteredAndSorted.findIndex(
                (opp) => opp.opportunityId === opportunityId
            );

            if (index !== -1) {
                const targetPage =
                    Math.floor(
                        index / state.opportunities.pagination.sizePerPage
                    ) + 1;
                state.opportunities.pagination.page = targetPage;
                updateTableData(state, serviceFocuses);
            }
        },
    },
    extraReducers(builder) {
        builder
            .addCase(GetOpportunities.pending, (state) => {
                setPendingState(state);
            })
            .addCase(GetOpportunities.rejected, (state) => {
                setRejectedState(state);
            })
            .addCase(GetOpportunities.fulfilled, (state, action) => {
                const { opportunities } = action.payload;
                state.opportunities.opportunities = opportunities;

                const updatedSelectedOpportuinity = (
                    opportunities as MarketplaceOpportunity[]
                ).find(
                    (f) =>
                        f.opportunityId ===
                        state.selectedOpportuity?.opportunityId
                );
                if (updatedSelectedOpportuinity) {
                    state.selectedOpportuity = updatedSelectedOpportuinity;
                }

                updateTableData(state);

                setSucceededState(state);
            })
            .addCase(GetOpportunity.pending, (state) => {
                setPendingState(state);
            })
            .addCase(GetOpportunity.rejected, (state) => {
                setRejectedState(state);
            })
            .addCase(
                GetAvailableOpportunitiesForTeamMembers.fulfilled,
                (state, action) => {
                    const {
                        availableOpportunitiesForTeamMembers:
                            availableOpportunitiesForTeamMembers,
                    } = action.payload;
                    state.availableOpportunitiesForTeamMembers =
                        availableOpportunitiesForTeamMembers;
                }
            )
            .addCase(GetOpportunity.fulfilled, (state, action) => {
                const { opportunity } = action.payload;
                const index = state.opportunities.opportunities.findIndex(
                    (o) => o.opportunityId === opportunity.opportunityId
                );

                const updatedOpportuinity = {
                    ...opportunity,
                    documentTables:
                        state.opportunities.opportunities[index]?.documentTables
                        ?? opportunity.documentTables,
                };

                if (index !== -1) {
                    state.opportunities.opportunities[index] =
                        updatedOpportuinity;
                    updateTableData(state);
                }

                state.selectedOpportuity = updatedOpportuinity;

                setSucceededState(state);
            })
            .addCase(GetOpportunityDocuments.pending, (state) => {
                setPendingState(state);
            })
            .addCase(GetOpportunityDocuments.rejected, (state) => {
                setRejectedState(state);
            })
            .addCase(GetOpportunityDocuments.fulfilled, (state, action) => {
                const {
                    sectionId,
                    documents,
                    pageNumber,
                    totalItems,
                    pageSize,
                    opportunityId,
                } = action.payload;
                const opportunity = state.opportunities.opportunities.find(
                    (opp) => opp.opportunityId === opportunityId
                );

                if (opportunity) {
                    opportunity.documentTables =
                        opportunity.documentTables || {};
                    opportunity.documentTables[sectionId] = {
                        documents,
                        pageNumber,
                        totalItems,
                        pageSize,
                    };
                    state.selectedOpportuity = opportunity;
                }
                setSucceededState(state);
            })
            .addCase(GetCurrentEventsOfAllOpportunities.pending, (state) => {
                setPendingState(state);
            })
            .addCase(GetCurrentEventsOfAllOpportunities.rejected, (state) => {
                setRejectedState(state);
            })
            .addCase(
                GetCurrentEventsOfAllOpportunities.fulfilled,
                (state, action) => {
                    const { events, pageNumber, totalItems, pageSize } =
                        action.payload;
                    state.currentEventsOfAllOpportunities.events = events;
                    state.currentEventsOfAllOpportunities.pageNumber =
                        pageNumber;
                    state.currentEventsOfAllOpportunities.totalItems =
                        totalItems;
                    state.currentEventsOfAllOpportunities.pageSize = pageSize;
                    setSucceededState(state);
                }
            );
    },
});

const setRejectedState = (state: any) => {
    state.isLoading = false;
    state.status = 'failed';
    state.error = state;
};

const setPendingState = (state: any) => {
    state.isLoading = true;
    state.status = 'loading';
    state.error = '';
};

const setSucceededState = (state: any) => {
    state.isLoading = false;
    state.status = 'succeeded';
    state.error = '';
};

export const {
    setSelectedOpportunity,
    resetState,
    setTableState,
    setPagination,
    setSortOrder,
    setFilters,
    navigateTableToOpportunity,
} = opportunitiesSlice.actions;
export default opportunitiesSlice.reducer;
